import type { BillingTerm, PricingShape } from '../models/pricing.model';
import type { ProductSlug } from '../models/product.model';
import { seatCommitAmount, seatMonthlyAmount, type SeatAnchors } from '../utils/seat-pricing';
import { ONBOARDING } from './site.config';

export const BILLING_CURRENCY = 'USD';

/** Resolved to a translated suffix via `pricing.units.<unit>`. */
export type PriceUnit = 'month' | 'userMonth' | 'deviceMonth';

export interface AmountConfig {
  /** Amount in major currency units. Null means "talk to us". */
  readonly amount: number | null;
  readonly currency: string;
  readonly unit: PriceUnit;
}

const amount = (monthly: number, commit: number, unit: PriceUnit): Record<BillingTerm, AmountConfig> => ({
  monthly: { amount: monthly, currency: BILLING_CURRENCY, unit },
  commit12: { amount: commit, currency: BILLING_CURRENCY, unit },
});

const contact: Record<BillingTerm, AmountConfig> = {
  monthly: { amount: null, currency: BILLING_CURRENCY, unit: 'month' },
  commit12: { amount: null, currency: BILLING_CURRENCY, unit: 'month' },
};

export interface PlanLineConfig {
  /** Key under `pricing.groups.<group>.plans.<code>.lines`. */
  readonly key: string;
  readonly price: Record<BillingTerm, AmountConfig>;
}

export interface PlanConfig {
  readonly code: string;
  readonly userCap?: number | null;
  readonly price?: Record<BillingTerm, AmountConfig>;
  readonly lines?: readonly PlanLineConfig[];
  readonly cta: { readonly href: string; readonly external?: boolean };
  readonly featured?: boolean;
  readonly contactOnly?: boolean;
}

export interface PricingGroupConfig {
  readonly key: string;
  readonly productSlug: ProductSlug;
  readonly accent: 'core' | 'catalog' | 'brand';
  readonly shape: PricingShape;
  readonly plans: readonly PlanConfig[];
  /**
   * Length of the free trial, in days. Catalog sets the subscription's trial
   * end to thirty days at checkout, on every plan and both billing terms.
   * Omitted where there is no trial to state.
   */
  readonly trialDays?: number;
  readonly seats?: SeatSliderConfig;
}

export interface SeatSliderConfig {
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly anchors: SeatAnchors;
  readonly commitDiscountPercent: number;
}

export const CATALOG_SEATS: SeatSliderConfig = {
  min: 200,
  max: 5000,
  step: 100,
  anchors: { at200: 299, at1000: 799, at5000: 2399 },
  commitDiscountPercent: 15,
};

export function fallbackSeatAmounts(seats: number, config: SeatSliderConfig = CATALOG_SEATS): Record<BillingTerm, number> {
  const monthly = seatMonthlyAmount(seats, config.anchors);

  return { monthly, commit12: seatCommitAmount(monthly, config.commitDiscountPercent) };
}

const catalogFrom = fallbackSeatAmounts(CATALOG_SEATS.min);

/** The device licence costs the same on every Core plan — one object, shared. */
const deviceLicence: PlanLineConfig = {
  key: 'deviceLicence',
  price: amount(2, 1.7, 'deviceMonth'),
};

/** Catalog's amounts are the fallback for its live rates; Core's are illustrative placeholders. */
export const PRICING_CONFIG: readonly PricingGroupConfig[] = [
  {
    key: 'catalog',
    productSlug: 'catalog',
    accent: 'catalog',
    shape: 'org-limit',
    trialDays: 30,
    seats: CATALOG_SEATS,
    plans: [
      { code: 'catalog', userCap: CATALOG_SEATS.min, price: amount(catalogFrom.monthly, catalogFrom.commit12, 'month'),
        cta: { href: ONBOARDING.catalog, external: true } },
      { code: 'catalog_custom', userCap: null, price: contact,
        cta: { href: '/contact?product=catalog' }, contactOnly: true },
    ],
  },
  {
    key: 'core',
    productSlug: 'core',
    accent: 'core',
    shape: 'per-licence',
    plans: [
      { code: 'core_essentials', price: amount(4, 3.3, 'userMonth'), lines: [deviceLicence],
        cta: { href: '/contact?product=core&plan=essentials' } },
      { code: 'core_advanced', price: amount(7, 5.8, 'userMonth'), lines: [deviceLicence],
        cta: { href: '/contact?product=core&plan=advanced' }, featured: true },
      { code: 'core_complete', price: amount(11, 9.1, 'userMonth'), lines: [deviceLicence],
        cta: { href: '/contact?product=core&plan=complete' } },
    ],
  },
];
